import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useFieldPermissions } from "@/hooks/useFieldPermissions";
import { InlineEditableField } from "@/components/InlineEditableField";
import { InlineEditableSection } from "@/components/InlineEditableSection";
import { apiGetSalary, apiUpdateSalary, apiRequestSalaryIncrease, apiGetCompanyCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { DollarSign, Hash, Calendar, Clock, TrendingUp, Percent } from "lucide-react";
import type { EmployeeProfile } from "@/data/employees";

interface Props {
  employee: EmployeeProfile;
  isOwnProfile: boolean;
  isLineManagerOfEmployee?: boolean;
}

export function ProfileSalaryTab({ employee, isOwnProfile, isLineManagerOfEmployee = false }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canEdit, canEditAnyInSection } = useFieldPermissions();
  const viewerRole = user?.role ?? { mainRole: "user" as const, subRole: "employee" as const };

  const { data: salary } = useQuery({
    queryKey: ["salary", employee.id],
    queryFn: () => apiGetSalary(employee.id),
  });

  const { data: currencyData } = useQuery({
    queryKey: ["company-currency"],
    queryFn: apiGetCompanyCurrency,
  });
  const companyCurrency = currencyData?.currency ?? "EUR";

  const [form, setForm] = useState({
    baseSalary: "",
    payFrequency: "Monthly",
    bonus: "",
    effectiveDate: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showIncreaseDialog, setShowIncreaseDialog] = useState(false);

  useEffect(() => {
    if (salary) {
      setForm({
        baseSalary: salary.baseSalary != null ? String(salary.baseSalary) : "",
        payFrequency: salary.payFrequency || "Monthly",
        bonus: salary.bonus != null ? String(salary.bonus) : "",
        effectiveDate: salary.effectiveDate || "",
      });
    }
  }, [salary]);

  const mutation = useMutation({
    mutationFn: () => apiUpdateSalary(employee.id, {
      baseSalary: form.baseSalary ? Number(form.baseSalary) : undefined,
      payFrequency: form.payFrequency,
      bonus: form.bonus ? Number(form.bonus) : undefined,
      effectiveDate: form.effectiveDate || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setIsEditing(false);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  const ce = (field: string) => canEdit(field, viewerRole, isOwnProfile, isLineManagerOfEmployee);

  // Line managers with full profile access can request salary increases
  const canRequestIncrease = isLineManagerOfEmployee && user?.fullProfileAccess !== false;

  return (
    <div className="max-w-2xl">
      <InlineEditableSection
        title={t("profile.tabs.salary")}
        icon={DollarSign}
        sectionKey="salary"
        canEditAny={canEditAnyInSection("salary", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
        isEditing={isEditing}
        onEdit={() => setIsEditing(true)}
        onSave={() => mutation.mutate()}
        onCancel={() => { setIsEditing(false); if (salary) setForm({ baseSalary: salary.baseSalary != null ? String(salary.baseSalary) : "", payFrequency: salary.payFrequency || "Monthly", bonus: salary.bonus != null ? String(salary.bonus) : "", effectiveDate: salary.effectiveDate || "" }); }}
        isSaving={mutation.isPending}
      >
        <InlineEditableField label={t("profile.salary.baseSalary")} value={form.baseSalary} fieldName="base_salary" type="number" isEditing={isEditing} canEdit={ce("base_salary")} onChange={(_, v) => setForm((p) => ({ ...p, baseSalary: v }))} icon={DollarSign} />
        {/* Currency is company-wide — set in Payroll → Tax Settings, not per employee */}
        <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Hash className="h-4 w-4 shrink-0" />
            <span>{t("profile.salary.currency")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{companyCurrency}</span>
            <span className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5">Company-wide</span>
          </div>
        </div>
        <InlineEditableField label={t("profile.salary.payFrequency")} value={form.payFrequency} fieldName="pay_frequency" type="select" options={[{ value: "Monthly", label: "Monthly" }, { value: "Bi-weekly", label: "Bi-weekly" }, { value: "Weekly", label: "Weekly" }]} isEditing={isEditing} canEdit={ce("pay_frequency")} onChange={(_, v) => setForm((p) => ({ ...p, payFrequency: v }))} icon={Clock} />
        <InlineEditableField label={t("profile.salary.bonus")} value={form.bonus} fieldName="bonus" type="number" isEditing={isEditing} canEdit={ce("bonus")} onChange={(_, v) => setForm((p) => ({ ...p, bonus: v }))} icon={DollarSign} />
        <InlineEditableField label={t("profile.salary.effectiveDate")} value={form.effectiveDate} fieldName="effective_date" type="date" isEditing={isEditing} canEdit={ce("effective_date")} onChange={(_, v) => setForm((p) => ({ ...p, effectiveDate: v }))} icon={Calendar} />
      </InlineEditableSection>

      {/* Request Salary Increase — line managers with full profile access */}
      {canRequestIncrease && !isEditing && (
        <div className="mt-4">
          <Button
            variant="outline"
            onClick={() => setShowIncreaseDialog(true)}
            className="gap-2"
          >
            <TrendingUp className="w-4 h-4" />
            {t("profile.salary.requestIncrease")}
          </Button>
        </div>
      )}

      {/* Salary Increase Request Dialog */}
      <SalaryIncreaseDialog
        open={showIncreaseDialog}
        onClose={() => setShowIncreaseDialog(false)}
        employee={employee}
        currentSalary={salary}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SALARY INCREASE REQUEST DIALOG
// ════════════════════════════════════════════════════════════════════

function SalaryIncreaseDialog({
  open, onClose, employee, currentSalary,
}: {
  open: boolean;
  onClose: () => void;
  employee: EmployeeProfile;
  currentSalary?: { baseSalary: number | null; currency: string; payFrequency: string; bonus: number | null; effectiveDate: string | null } | null;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [increaseMode, setIncreaseMode] = useState<"fixed" | "percentage">("fixed");
  const [increaseValue, setIncreaseValue] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [payFrequency, setPayFrequency] = useState("Monthly");
  const [bonus, setBonus] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill from current salary when dialog opens
  useEffect(() => {
    if (open && currentSalary) {
      setCurrency(currentSalary.currency || "EUR");
      setPayFrequency(currentSalary.payFrequency || "Monthly");
      setBonus(currentSalary.bonus ? String(currentSalary.bonus) : "");
      setIncreaseValue("");
      setIncreaseMode("fixed");
      setEffectiveDate("");
      setReason("");
    }
  }, [open, currentSalary]);

  const currentAmount = currentSalary?.baseSalary ?? 0;
  const increaseNum = increaseValue ? Number(increaseValue) : 0;
  const newAmount = increaseMode === "fixed"
    ? currentAmount + increaseNum
    : currentAmount > 0 ? currentAmount * (1 + increaseNum / 100) : 0;
  const diff = newAmount - currentAmount;
  const diffPercent = currentAmount > 0 ? ((diff / currentAmount) * 100).toFixed(1) : "0";

  const isValid = increaseNum > 0 && newAmount > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      await apiRequestSalaryIncrease({
        employeeId: Number(employee.id),
        baseSalary: Math.round(newAmount * 100) / 100,
        currency,
        payFrequency,
        bonus: bonus ? Number(bonus) : 0,
        effectiveDate: effectiveDate || undefined,
        reason: reason || undefined,
      });
      toast({ title: t("profile.salary.increaseSubmitted") });
      onClose();
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            {t("profile.salary.requestIncrease")}
          </DialogTitle>
          <DialogDescription>
            {t("profile.salary.requestIncreaseDesc", { name: employee.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Left column — increase calculator */}
          <div className="space-y-4">
            {/* Current salary */}
            {currentSalary && currentAmount > 0 && (
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs text-muted-foreground mb-1">{t("profile.salary.currentSalary")}</p>
                <p className="text-lg font-semibold text-foreground">
                  {currentAmount.toLocaleString()} {currentSalary.currency || "EUR"}
                </p>
              </div>
            )}

            {/* Increase type toggle */}
            <div>
              <Label>{t("profile.salary.increaseType")}</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => { setIncreaseMode("fixed"); setIncreaseValue(""); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-md border py-1.5 text-sm transition-colors ${increaseMode === "fixed" ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-accent/50"}`}
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  {t("profile.salary.fixedAmount")}
                </button>
                <button
                  type="button"
                  onClick={() => { setIncreaseMode("percentage"); setIncreaseValue(""); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-md border py-1.5 text-sm transition-colors ${increaseMode === "percentage" ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-accent/50"}`}
                >
                  <Percent className="w-3.5 h-3.5" />
                  {t("profile.salary.percentage")}
                </button>
              </div>
            </div>

            {/* Increase value + live preview */}
            <div>
              <FloatWrapper label={increaseMode === "fixed" ? t("profile.salary.increaseAmount") : t("profile.salary.increasePercentage")} required>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step={increaseMode === "percentage" ? "0.1" : "1"}
                    value={increaseValue}
                    onChange={(e) => setIncreaseValue(e.target.value)}
                    placeholder={increaseMode === "fixed" ? "e.g. 5000" : "e.g. 10"}
                    className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 pr-16 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                    {increaseMode === "fixed" ? (currency || "EUR") : "%"}
                  </span>
                </div>
              </FloatWrapper>
              {increaseNum > 0 && currentAmount > 0 && (
                <div className="mt-2 rounded-md bg-muted/50 p-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{t("profile.salary.newSalary")}</span>
                    <span className="font-semibold text-foreground">{Math.round(newAmount).toLocaleString()} {currency || "EUR"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Change</span>
                    <span className={diff > 0 ? "text-green-600 dark:text-green-400 font-medium" : "text-red-600 dark:text-red-400 font-medium"}>
                      {diff > 0 ? "+" : ""}{Math.round(diff).toLocaleString()} ({diff > 0 ? "+" : ""}{diffPercent}%)
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column — details */}
          <div>
            {/* Currency */}
            <FloatWrapper label={t("profile.salary.currency")}>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["EUR", "USD", "GBP", "SGD", "CHF"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FloatWrapper>

            {/* Effective date */}
            <FloatWrapper label={t("profile.salary.effectiveDate")}>
              <DatePicker value={effectiveDate} onChange={setEffectiveDate} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
            </FloatWrapper>

            {/* Reason */}
            <FloatField
              label={t("profile.salary.reason")}
              type="textarea"
              value={reason}
              onChange={(v) => setReason(v as string)}
              placeholder={t("profile.salary.reasonPlaceholder")}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={submitting || !isValid}>
            {submitting ? t("common.saving") : t("profile.salary.submitRequest")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
